import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Bell,
  Plus,
  Check,
  X,
  Clock,
  AlertTriangle,
  CalendarClock,
  Trash2,
  User,
  Repeat,
  Filter,
} from 'lucide-react'
import { getReminderService } from '@/services/reminders'
import { toast } from '@/store/toastStore'
import AddReminderModal from '@/components/customers/AddReminderModal'
import {
  isOpenReminder,
  REMINDER_PRIORITY_LABEL,
  REMINDER_CHANNEL_LABEL,
  REMINDER_RECURRENCE_LABEL,
} from '@/types/reminder.types'
import type { Reminder, ReminderPriority } from '@/types/reminder.types'
import type { IndividualCustomer } from '@/types/customer'
import { useAuth } from '@/contexts/AuthContext'

const ENTITY_TYPE = 'individual_customer'
const SNOOZE_MINUTES = 24 * 60 // 1 day

interface CustomerRemindersTabProps {
  customer: IndividualCustomer
  /** When true (set by the Overview Quick Action), open the add-reminder modal on mount. */
  autoCreate?: boolean
  /** Called once the auto-open has been handled, so the parent can clear its flag. */
  onAutoCreateHandled?: () => void
}

function priorityBadgeClass(priority: ReminderPriority): string {
  switch (priority) {
    case 'urgent':
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
    case 'high':
      return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
    case 'medium':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
    default:
      return 'bg-surface-overlay text-text-secondary'
  }
}

function formatDue(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function isOverdue(r: Reminder): boolean {
  return r.isOverdue ?? new Date(r.dueDate).getTime() < Date.now()
}

interface ReminderCardProps {
  reminder: Reminder
  onComplete?: (r: Reminder) => void
  onDismiss?: (r: Reminder) => void
  onSnooze?: (r: Reminder) => void
  onDelete?: (r: Reminder) => void
}

function ReminderCard({ reminder, onComplete, onDismiss, onSnooze, onDelete }: ReminderCardProps) {
  const open = isOpenReminder(reminder)
  const overdue = open && isOverdue(reminder)

  return (
    <div className="card card-elevated p-4">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-base font-semibold text-text-primary">{reminder.title}</h4>
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full ${priorityBadgeClass(
                reminder.priority
              )}`}
            >
              {REMINDER_PRIORITY_LABEL[reminder.priority] ?? reminder.priority}
            </span>
            {reminder.recurrence && reminder.recurrence !== 'none' && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border border-blue-200 dark:border-blue-800 flex items-center gap-1">
                <Repeat className="w-3 h-3" />
                {REMINDER_RECURRENCE_LABEL[reminder.recurrence] ?? reminder.recurrence}
              </span>
            )}
            {reminder.status === 'completed' && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                Completed
              </span>
            )}
            {reminder.status === 'dismissed' && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-surface-overlay text-text-tertiary">
                Dismissed
              </span>
            )}
          </div>

          {reminder.description && (
            <p className="text-sm text-text-secondary mt-1">{reminder.description}</p>
          )}

          <div className="flex items-center gap-3 mt-2 text-sm text-text-tertiary flex-wrap">
            <span
              className={`flex items-center gap-1 ${overdue ? 'text-red-600 dark:text-red-400' : ''}`}
            >
              {overdue ? (
                <AlertTriangle className="w-3.5 h-3.5" />
              ) : (
                <Clock className="w-3.5 h-3.5" />
              )}
              {overdue ? 'Overdue · ' : ''}
              {formatDue(reminder.dueDate)}
            </span>

            <span className="flex items-center gap-1">
              <User className="w-3.5 h-3.5" />
              {reminder.assignedToName ? `Assigned to ${reminder.assignedToName}` : 'Unassigned'}
            </span>

            {reminder.notifyChannels?.length > 0 && (
              <span>
                {reminder.notifyChannels.map(c => REMINDER_CHANNEL_LABEL[c] ?? c).join(', ')}
              </span>
            )}
          </div>
        </div>

        {open && (onComplete || onSnooze || onDismiss) && (
          <div className="flex items-center gap-1 flex-shrink-0">
            {onComplete && (
              <button
                type="button"
                onClick={() => onComplete(reminder)}
                className="btn btn-ghost btn-sm btn-square"
                title="Mark complete"
              >
                <Check className="w-4 h-4 text-green-600" />
              </button>
            )}
            {onSnooze && (
              <button
                type="button"
                onClick={() => onSnooze(reminder)}
                className="btn btn-ghost btn-sm btn-square"
                title="Snooze 1 day"
              >
                <CalendarClock className="w-4 h-4 text-text-secondary" />
              </button>
            )}
            {onDismiss && (
              <button
                type="button"
                onClick={() => onDismiss(reminder)}
                className="btn btn-ghost btn-sm btn-square"
                title="Dismiss"
              >
                <X className="w-4 h-4 text-text-secondary" />
              </button>
            )}
          </div>
        )}
        {!open && onDelete && (
          <button
            type="button"
            onClick={() => onDelete(reminder)}
            className="btn btn-ghost btn-sm btn-square flex-shrink-0"
            title="Delete"
          >
            <Trash2 className="w-4 h-4 text-text-tertiary" />
          </button>
        )}
      </div>
    </div>
  )
}

export default function CustomerRemindersTab({
  customer,
  autoCreate,
  onAutoCreateHandled,
}: CustomerRemindersTabProps) {
  const { hasPermission } = useAuth()
  const canCreate = hasPermission('txh.reminder.create')
  const canUpdate = hasPermission('txh.reminder.update')
  const canUpdateStatus = hasPermission('txh.reminder.status.update')
  const canDelete = hasPermission('txh.reminder.delete')
  const [reminders, setReminders] = useState<Reminder[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [filterAssignee, setFilterAssignee] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<string>('all')

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError('')
    try {
      const items = await getReminderService().listForEntity(ENTITY_TYPE, customer.id)
      setReminders(items)
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Please try again.')
      toast.error(
        'Could not load reminders',
        err instanceof Error ? err.message : 'Please try again.'
      )
    } finally {
      setLoading(false)
    }
  }, [customer.id])

  useEffect(() => {
    load()
  }, [load])

  // Honor the Overview "Set Reminder" Quick Action: open the add modal once.
  useEffect(() => {
    if (autoCreate && canCreate) {
      setShowAdd(true)
      onAutoCreateHandled?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCreate])

  const replace = (updated: Reminder) =>
    setReminders(prev => prev.map(r => (r.id === updated.id ? updated : r)))

  const handleComplete = async (r: Reminder) => {
    if (!canUpdateStatus) return
    try {
      const updated = await getReminderService().update(r.id, { status: 'completed' })
      replace(updated)
      toast.success('Reminder completed', r.title)
    } catch (err) {
      toast.error(
        'Could not complete reminder',
        err instanceof Error ? err.message : 'Please try again.'
      )
    }
  }

  const handleDismiss = async (r: Reminder) => {
    if (!canUpdateStatus) return
    try {
      const updated = await getReminderService().update(r.id, { status: 'dismissed' })
      replace(updated)
      toast.success('Reminder dismissed', r.title)
    } catch (err) {
      toast.error(
        'Could not dismiss reminder',
        err instanceof Error ? err.message : 'Please try again.'
      )
    }
  }

  const handleSnooze = async (r: Reminder) => {
    if (!canUpdate) return
    try {
      const updated = await getReminderService().update(r.id, { snoozeMinutes: SNOOZE_MINUTES })
      replace(updated)
      toast.success('Snoozed for 1 day', r.title)
    } catch (err) {
      toast.error(
        'Could not snooze reminder',
        err instanceof Error ? err.message : 'Please try again.'
      )
    }
  }

  const handleDelete = async (r: Reminder) => {
    if (!canDelete) return
    if (!window.confirm(`Delete reminder "${r.title}"?`)) return
    try {
      await getReminderService().remove(r.id)
      setReminders(prev => prev.filter(x => x.id !== r.id))
      toast.success('Reminder deleted', r.title)
    } catch (err) {
      toast.error(
        'Could not delete reminder',
        err instanceof Error ? err.message : 'Please try again.'
      )
    }
  }

  // Derive unique assignees
  const assignees = useMemo(() => {
    const map = new Map<string, string>()
    for (const r of reminders) {
      if (r.assignedToUserId && r.assignedToName) {
        map.set(r.assignedToUserId, r.assignedToName)
      }
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }))
  }, [reminders])

  // Filtered reminders
  const filteredReminders = useMemo(() => {
    return reminders.filter(r => {
      if (filterAssignee !== 'all') {
        if (filterAssignee === 'unassigned' && r.assignedToUserId) return false
        if (filterAssignee !== 'unassigned' && r.assignedToUserId !== filterAssignee) return false
      }
      if (filterStatus !== 'all') {
        const open = isOpenReminder(r)
        if (filterStatus === 'open' && !open) return false
        if (filterStatus === 'overdue' && (!open || !isOverdue(r))) return false
        if (filterStatus === 'upcoming' && (!open || isOverdue(r))) return false
        if (filterStatus === 'completed' && r.status !== 'completed') return false
      }
      return true
    })
  }, [reminders, filterAssignee, filterStatus])

  const open = filteredReminders.filter(isOpenReminder)
  const overdue = open.filter(isOverdue).sort((a, b) => +new Date(a.dueDate) - +new Date(b.dueDate))
  const upcoming = open
    .filter(r => !isOverdue(r))
    .sort((a, b) => +new Date(a.dueDate) - +new Date(b.dueDate))
  const closed = filteredReminders.filter(r => !isOpenReminder(r))

  const cardProps = {
    onComplete: canUpdateStatus ? handleComplete : undefined,
    onDismiss: canUpdateStatus ? handleDismiss : undefined,
    onSnooze: canUpdate ? handleSnooze : undefined,
    onDelete: canDelete ? handleDelete : undefined,
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h3 className="text-lg font-semibold text-text-primary">Reminders</h3>

        <div className="flex flex-wrap items-center gap-3">
          {/* Status filter */}
          <div className="flex items-center gap-1.5">
            <Filter className="w-3.5 h-3.5 text-text-tertiary" />
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              className="input input-bordered text-sm !w-auto max-w-full"
              aria-label="Filter by Status"
            >
              <option value="all">All Statuses</option>
              <option value="open">Open (Overdue + Upcoming)</option>
              <option value="overdue">Overdue Only</option>
              <option value="upcoming">Upcoming Only</option>
              <option value="completed">Completed Only</option>
            </select>
          </div>

          {/* Assignee filter */}
          <select
            value={filterAssignee}
            onChange={e => setFilterAssignee(e.target.value)}
            className="input input-bordered text-sm !w-auto max-w-full"
            aria-label="Filter by Assignee"
          >
            <option value="all">All Assignees</option>
            <option value="unassigned">Unassigned</option>
            {assignees.map(a => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>

          {canCreate && (
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="btn btn-primary btn-sm gap-2"
            >
              <Plus className="w-4 h-4" />
              Add reminder
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="card card-elevated p-4">
              <div className="skeleton h-4 w-1/3 rounded mb-2" />
              <div className="skeleton h-3 w-1/2 rounded" />
            </div>
          ))}
        </div>
      ) : loadError ? (
        <div role="alert" className="card card-elevated p-6 space-y-3">
          <p className="text-red-600 dark:text-red-400">Could not load reminders: {loadError}</p>
          <button type="button" className="btn btn-outline" onClick={load}>
            Retry
          </button>
        </div>
      ) : reminders.length === 0 ? (
        <div className="card card-elevated p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-surface-overlay flex items-center justify-center mx-auto mb-4">
            <Bell className="w-8 h-8 text-text-tertiary" />
          </div>
          <p className="text-lg font-medium text-text-primary mb-2">No reminders yet</p>
          <p className="text-text-secondary">
            Add a reminder to keep track of this client's deadlines.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {overdue.length > 0 && (
            <section className="space-y-3">
              <h4 className="text-sm font-semibold text-red-600 dark:text-red-400 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Overdue ({overdue.length})
              </h4>
              {overdue.map(r => (
                <ReminderCard key={r.id} reminder={r} {...cardProps} />
              ))}
            </section>
          )}

          {upcoming.length > 0 && (
            <section className="space-y-3">
              <h4 className="text-sm font-semibold text-text-secondary flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Upcoming ({upcoming.length})
              </h4>
              {upcoming.map(r => (
                <ReminderCard key={r.id} reminder={r} {...cardProps} />
              ))}
            </section>
          )}

          {closed.length > 0 && (
            <section className="space-y-3">
              <h4 className="text-sm font-semibold text-text-tertiary">
                Completed & dismissed ({closed.length})
              </h4>
              {closed.map(r => (
                <ReminderCard key={r.id} reminder={r} {...cardProps} />
              ))}
            </section>
          )}
        </div>
      )}

      {canCreate && (
        <AddReminderModal
          isOpen={showAdd}
          onClose={() => setShowAdd(false)}
          onSubmit={async input => {
            const created = await getReminderService().create({
              ...input,
              entityType: ENTITY_TYPE,
              entityId: customer.id,
            })
            setReminders(prev => [created, ...prev])
            toast.success('Reminder added', created.title)
          }}
        />
      )}
    </div>
  )
}
